import React from 'react';
import { decomposeColor } from '@mui/material/styles';
import { styled, PeekBox, PeekTypography } from '@piiqtechnologies/ui-components';

// What the card, the demo player and the dialog's controls share: colours, the buttons and the
// painted surface. Internal to the widget; not exported from the package. Tokens from the pop-up
// template (Figma "02 — POP-UP TEMPLATES").
const MUTED = "#d2d2d7";
/** The headline's face: Inter Light, which Discover loads. */
const HEADLINE_FONT = "Inter, Roboto, sans-serif";
/**
 * A bloom's fade, as Figma blends it: straight colour, so the colour drifts from `from` to `to` as it
 * clears. CSS blends through premultiplied colour, which would keep `from` all the way out, so the
 * stops in between are spelled out.
 */
const bloomFade = ({ from, to }) => {
    const [start, end] = [from, to].map((color) => decomposeColor(color).values);
    const steps = from === to ? [0, 1] : [0, 0.25, 0.5, 0.75, 1];
    const stops = steps.map((step) => {
        const [red, green, blue] = start.map((value, channel) => Math.round(value + (end[channel] - value) * step));
        return `rgba(${red}, ${green}, ${blue}, ${1 - step}) ${step * 100}%`;
    });
    return `radial-gradient(closest-side, ${stops.join(", ")})`;
};
/** One blurred glow, placed as the template places it. */
const Bloom = ({ bloom }) => (React.createElement(PeekBox, { sx: Object.assign({ position: "absolute", left: 0, top: 0, width: bloom.width, height: bloom.height, transformOrigin: "0 0", transform: bloom.transform, opacity: bloom.opacity, filter: `blur(${bloom.blur}px)` }, (bloom.fade ? { borderRadius: "50%", overflow: "hidden" } : { backgroundImage: bloomFade(bloom) })) }, bloom.fade && (React.createElement(PeekBox, { sx: {
        position: "absolute",
        inset: 0,
        transformOrigin: "0 0",
        transform: bloom.fade,
        backgroundImage: bloomFade(bloom),
    } }))));
/** A treatment's paint (its background and blooms), behind everything else on the card. */
const TreatmentPaint = ({ paint, sx }) => (React.createElement(PeekBox, { "aria-hidden": true, sx: Object.assign({ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", background: paint.background }, sx) }, paint.blooms.map((bloom, index) => (React.createElement(Bloom, { key: index, bloom: bloom })))));
/** The card's full-width buttons: the main one in the treatment's accent, the second outlined. */
const CardButton = styled("button")(({ theme }) => (Object.assign(Object.assign({}, theme.typography.h4), { boxSizing: "border-box", width: "100%", height: 38, margin: 0, padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 5, cursor: "pointer", whiteSpace: "nowrap", "& svg": { width: 18, height: 18, flexShrink: 0 }, "&:focus-visible": { outline: "2px solid #ffffff", outlineOffset: 2 } })));
/**
 * The X in the card's top right corner, over the picture when there is one and beside the words
 * when there isn't: closes the pop-up from any slide. Dark glass, so it reads on any picture.
 */
const CardCloseButton = styled("button")({
    position: "absolute",
    top: 24,
    right: 19,
    zIndex: 2,
    width: 32,
    height: 32,
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: "50%",
    backgroundColor: "rgba(10, 10, 25, 0.55)",
    color: "#ffffff",
    cursor: "pointer",
    transition: "background-color 150ms ease",
    "& svg": { width: 16, height: 16 },
    "&:hover": { backgroundColor: "rgba(10, 10, 25, 0.75)" },
    "&:focus-visible": { outline: "2px solid #ffffff", outlineOffset: 2 },
});
/** The second button: "Next" while slides remain, "Close" on the last. */
const secondaryButtonStyle = {
    border: "1px solid rgba(255, 255, 255, 0.3)",
    backgroundColor: "rgba(255, 255, 255, 0.06)",
    color: "#ffffff",
    fontWeight: 400,
};
/** The painted card: the treatment's paint, the template's padding and shadow. Shared with the demo player. */
const NotificationCardSurface = ({ paint, treatmentId, children, "data-testid": testId }) => (React.createElement(PeekBox, { "data-testid": testId, "data-treatment": treatmentId, sx: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        px: "11px",
        py: "16px",
        borderRadius: "12px",
        color: "common.white",
        boxShadow: "0 22px 48px -10px rgba(0, 0, 0, 0.42), 0 6px 26px -4px rgba(4, 210, 198, 0.1)",
    } },
    React.createElement(TreatmentPaint, { paint: paint }),
    children));
/** Inter Light 28/36, its end (usually the feature's name) in the highlight colour. The card's and the demo player's. */
const CardHeadline = ({ words, color, highlight, id }) => (React.createElement(PeekTypography, { id: id, variant: "h2", sx: {
        fontFamily: HEADLINE_FONT,
        fontSize: 28,
        lineHeight: "36px",
        fontWeight: 300,
        letterSpacing: 0,
        color,
        overflowWrap: "anywhere",
    } },
    words.headline,
    words.headlineHighlight && (React.createElement(React.Fragment, null,
        " ",
        React.createElement(PeekBox, { component: "span", sx: { color: highlight } }, words.headlineHighlight)))));
/** The small uppercase line above the headline, with its 14px icon ("🔈 New in Discover"). */
const CardEyebrow = ({ icon, color, children }) => (React.createElement(PeekTypography, { variant: "h5", "data-testid": "notification-card-eyebrow", sx: {
        display: "flex",
        alignItems: "center",
        gap: "3px",
        color,
        fontWeight: "fontWeightMedium",
        letterSpacing: "0.6px",
        textTransform: "uppercase",
    } },
    React.createElement(PeekBox, { component: "span", "aria-hidden": true, sx: { display: "flex", "& svg": { width: 14, height: 14 } } }, icon),
    children));

export { CardButton, CardCloseButton, CardEyebrow, CardHeadline, MUTED, NotificationCardSurface, TreatmentPaint, secondaryButtonStyle };
